import axios from 'axios';
import Cookies from 'js-cookie';
import {
  BASE_URL_GO,
  PRICE_BOOK_CHANNEL,
  PRICE_BOOK_RESOLVE,
  PRICE_BOOK_RESOLVE_BULK,
} from '../../Constants/Config';
import {
  normalizeUpc,
  parseUnknownUpcFromError,
  toPriceBookEntry,
} from './priceBookCore';

const PRICE_BOOK_TIMEOUT_MS = 5000;
const MAX_UNKNOWN_UPC_RETRIES = 3;

const getCustomerEmail = () => {
  if (Cookies.get('is_guest') === 'yes') return '';

  const loginData = Cookies.get('userLoginData');
  if (!loginData) return '';

  try {
    const customer = JSON.parse(loginData);
    return typeof customer?.email === 'string' ? customer.email.trim() : '';
  } catch {
    return '';
  }
};

const buildPayload = (merchantId) => {
  const customerEmail = getCustomerEmail();

  return {
    merchant_id: merchantId,
    location_merchant_id: merchantId,
    channel: PRICE_BOOK_CHANNEL,
    customer_group_id: null,
    ...(customerEmail ? { customer_email: customerEmail } : {}),
  };
};

const postPriceBook = (endpoint, body) =>
  axios.post(BASE_URL_GO + endpoint, body, {
    headers: { 'Content-Type': 'application/json' },
    timeout: PRICE_BOOK_TIMEOUT_MS,
  });

const readErrorMessage = (error) => error?.response?.data?.message || '';

export const fetchPriceBookPrice = async (merchantId, upc) => {
  try {
    const response = await postPriceBook(PRICE_BOOK_RESOLVE, {
      ...buildPayload(merchantId),
      upc,
    });
    const entry = toPriceBookEntry(response?.data?.data);
    return entry
      ? { entries: [entry], missingUpcs: [] }
      : { entries: [], missingUpcs: [upc] };
  } catch (error) {
    if (/upc not found/i.test(readErrorMessage(error))) {
      return { entries: [], missingUpcs: [upc] };
    }
    throw error;
  }
};

export const fetchPriceBookPrices = async (merchantId, upcs) => {
  let pending = [...upcs];
  const missingUpcs = [];

  for (let attempt = 0; pending.length > 0; attempt += 1) {
    try {
      const response = await postPriceBook(PRICE_BOOK_RESOLVE_BULK, {
        ...buildPayload(merchantId),
        upcs: pending,
      });
      const rows = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const entries = rows.map(toPriceBookEntry).filter(Boolean);
      const returnedUpcs = new Set(entries.map((entry) => entry.upc));

      return {
        entries,
        missingUpcs: [
          ...missingUpcs,
          ...pending.filter((upc) => !returnedUpcs.has(normalizeUpc(upc))),
        ],
      };
    } catch (error) {
      const unknownUpc = parseUnknownUpcFromError(readErrorMessage(error));
      if (
        !unknownUpc ||
        !pending.includes(unknownUpc) ||
        attempt >= MAX_UNKNOWN_UPC_RETRIES
      ) {
        throw error;
      }
      missingUpcs.push(unknownUpc);
      pending = pending.filter((upc) => upc !== unknownUpc);
    }
  }

  return { entries: [], missingUpcs };
};
